import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, App, Badge, Button, Drawer, Image, Spin, Switch, Table, Tabs, Tag, message } from 'antd';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import ImageUploader from 'components/ImageUploader';
import { StepKey as BizStepKey, resolveSteps } from 'pages/tms/task/config/taskTabStepConfig';
import InputFilterItem from 'components/InputFilterItem';
import SingleSelect from 'components/SingleSelect';
import {
    TMS_TASK_WAYPOINTS,
    TMS_TASK_WAYPOINT_LOADING_SUMMARY,
    TmsTaskWaypointItem,
    TmsTaskWaypointLoadingSummaryResponse,
    TmsTaskWaypointsResponse,
} from '../graphql/queries/taskManagementQueries';
import { TMS_TASK_FIELD_OPTIONS, TmsTaskFieldOptionsResponse } from '../graphql/queries/taskFieldEditQueries';
import {
    RECOMPUTE_TMS_STATIC_ETA,
    RecomputeTmsStaticEtaResponse,
    TmsTaskWaypointFieldsInput,
    UPDATE_TMS_TASK_WAYPOINT_FIELDS,
    UpdateTmsTaskWaypointFieldsResponse,
} from '../graphql/mutations/taskManagementMutations';
import {
    countChangedFields,
    OpsDateTimeItem,
    RELOCK_DISCARD_HINT,
    renderWaypointStatus,
    usableZone,
    UTC_PATTERN,
    WAYPOINT_STATUS_MAP,
    WAYPOINT_STATUS_ORDER,
    WAYPOINT_TYPE_MAP,
} from './taskManagementShared';

dayjs.extend(utc);
dayjs.extend(timezone);

type Props = {
    /** 为 null 时关闭 */
    task: { id: number; taskNumber: string | null; businessType?: number | null; taskStatus?: number | null; routeType?: number | null } | null;
    onClose: () => void;
};

// ---------- 字段定义 ----------

type ValueType = 'string' | 'int' | 'long' | 'date' | 'time' | 'datetime';

type FieldDef = {
    key: string;
    label: string;
    kind: 'text' | 'select';
    type: ValueType;
    options?: { label: string; value: string }[];
    /** 外键下拉：取自编辑字段抽屉同一份主数据选项 */
    optionsKey?: 'warehouses' | 'externalStations' | 'routes';
    /** 库表 NOT NULL 列：不可置空 */
    required?: boolean;
    /** 默认锁定（解除联动锁定后可改） */
    readOnly?: boolean;
    /** 本地日期/时间：由同组 UTC 时间点按途经点时区换算并锁定 */
    localOf?: string;
    placeholder?: string;
    /** 这些途经点类型下不展示（与业务侧步骤详情一致） */
    hideForWaypointTypes?: number[];
};

type PhotoDef = { keysKey: keyof TmsTaskWaypointItem; urlsKey: keyof TmsTaskWaypointItem; fieldKey: string; label: string; /** 这些途经点类型下不展示（与业务侧步骤详情一致） */ hideForWaypointTypes?: number[] };

type StepKey = 'arrived' | 'loading' | 'sealing' | 'departure' | 'unsealing' | 'unloading';
type CardKey = 'base' | StepKey;

type CardDef = { key: CardKey; title: string; fields: FieldDef[]; photos: PhotoDef[] };

const D = 'yyyy-MM-dd';
const T = 'HH:mm:ss';
const DT = 'yyyy-MM-dd HH:mm:ss';

/** 途经点顺序：当前路线最多 5 站（用户 2026-10-08：仅支持 0～4；文案带含义提示，提交仍为数字 0～4） */
const WAYPOINT_ORDER_OPTIONS = [
    { value: '0', label: '0 · 起点' },
    { value: '1', label: '1 · 途经点1' },
    { value: '2', label: '2 · 途经点2' },
    { value: '3', label: '3 · 途经点3' },
    { value: '4', label: '4 · 终点' },
];
/** waypoint_id = -1：三方站点（LAXSTEMU-LD 这类），实际站点在 ext_waypoint_id（external_station） */
const EXTERNAL_WAYPOINT_ID = '-1';
/** route_id = -1：三方任务（task_type 3）无路线，途经点随任务同为 -1 */
const THIRD_PARTY_ROUTE_ID = '-1';

const enumOptions = (map: Record<number, { label: string } | string>) =>
    Object.entries(map).map(([value, item]) => ({ value, label: typeof item === 'string' ? item : item.label }));

/** UTC 时间点 + 对应本地日期/时间 三件套 */
const utcTriple = (prefix: string, label: string, hideForWaypointTypes?: number[]): FieldDef[] => [
    { key: `${prefix}At`, label: `${label}(UTC)`, kind: 'text', type: 'datetime', placeholder: DT, hideForWaypointTypes },
    { key: `${prefix}Date`, label: `${label}日期`, kind: 'text', type: 'date', placeholder: D, localOf: `${prefix}At`, hideForWaypointTypes },
    { key: `${prefix}Time`, label: `${label}时间`, kind: 'text', type: 'time', placeholder: T, localOf: `${prefix}At`, hideForWaypointTypes },
];

const BASE_CARD: CardDef = {
    key: 'base',
    title: '途经点信息',
    fields: [
        { key: 'waypointStatus', label: '状态', kind: 'select', type: 'int', options: WAYPOINT_STATUS_ORDER.map((code) => ({ value: String(code), label: WAYPOINT_STATUS_MAP[code].label })), required: true },
        { key: 'waypointOrder', label: '顺序', kind: 'select', type: 'int', options: WAYPOINT_ORDER_OPTIONS, required: true, readOnly: true },
        { key: 'waypointType', label: '类型', kind: 'select', type: 'int', options: enumOptions(WAYPOINT_TYPE_MAP), required: true, readOnly: true },
        { key: 'waypointId', label: '途经点ID', kind: 'select', type: 'long', optionsKey: 'warehouses', required: true, readOnly: true },
        { key: 'extWaypointId', label: '三方站点ID', kind: 'select', type: 'long', optionsKey: 'externalStations', readOnly: true },
        { key: 'routeId', label: '路线ID', kind: 'select', type: 'long', optionsKey: 'routes', required: true, readOnly: true },
    ],
    photos: [],
};

const STEP_CARDS: Record<StepKey, CardDef> = {
    arrived: {
        key: 'arrived',
        title: '司机到达',
        fields: [
            ...utcTriple('planArrival', '计划到达'),
            // 始发站无静态 ETA（业务侧 DriverArrivedDetail 在 Origin 不展示），用户 2026-10-09
            ...utcTriple('staticEta', '静态ETA', [1]),
            ...utcTriple('actualArrival', '实际到达'),
            { key: 'arrivedRemark', label: '到达备注', kind: 'text', type: 'string' },
        ],
        photos: [
            { keysKey: 'arrivalPhotoKeys', urlsKey: 'arrivalPhotoUrls', fieldKey: 'arrivalPhotosJson', label: '到达照片' },
            // 始发站到达无封签照片（业务侧 DriverArrivedDetail 只在途经点 / 终点展示），用户 2026-10-09
            { keysKey: 'arrivalSealPhotoKeys', urlsKey: 'arrivalSealPhotoUrls', fieldKey: 'arrivalSealPhotosJson', label: '到达封签照片', hideForWaypointTypes: [1] },
        ],
    },
    loading: {
        key: 'loading',
        title: '装车',
        fields: [
            { key: 'loadingTime', label: '装车时间(UTC)', kind: 'text', type: 'datetime', placeholder: DT },
            { key: 'loadingInfoRemark', label: '装车备注', kind: 'text', type: 'string' },
        ],
        photos: [{ keysKey: 'loadingInteriorPhotoKeys', urlsKey: 'loadingInteriorPhotoUrls', fieldKey: 'loadingInfoInteriorPhotosJson', label: '车厢内照片' }],
    },
    sealing: {
        key: 'sealing',
        title: '封车',
        fields: [
            { key: 'sealTime', label: '封车时间(UTC)', kind: 'text', type: 'datetime', placeholder: DT },
            { key: 'sealNumber', label: '封签号', kind: 'text', type: 'string' },
            { key: 'sealAccountId', label: '封车账号ID', kind: 'text', type: 'long', readOnly: true },   // 保存封车时间时后端自动记操作人
        ],
        photos: [],
    },
    departure: {
        key: 'departure',
        title: '发车',
        fields: [
            ...utcTriple('planDeparture', '计划发车'),
            ...utcTriple('actualDeparture', '实际发车'),
            { key: 'departureRemark', label: '出发备注', kind: 'text', type: 'string' },
        ],
        photos: [
            { keysKey: 'departureSealPhotoKeys', urlsKey: 'departureSealPhotoUrls', fieldKey: 'departureSealPhotosJson', label: '封签照片' },
            { keysKey: 'departurePhotoKeys', urlsKey: 'departurePhotoUrls', fieldKey: 'departurePhotosJson', label: 'BOL照片' },
        ],
    },
    unsealing: {
        key: 'unsealing',
        title: '解封车',
        fields: [
            { key: 'unSealTime', label: '解封时间(UTC)', kind: 'text', type: 'datetime', placeholder: DT },
            { key: 'unSealAccountId', label: '解封账号ID', kind: 'text', type: 'long', readOnly: true },   // 保存解封时间时后端自动记操作人
        ],
        photos: [{ keysKey: 'unSealInteriorPhotoKeys', urlsKey: 'unSealInteriorPhotoUrls', fieldKey: 'unSealInteriorPhotosJson', label: '车厢内照片' }],
    },
    unloading: {
        key: 'unloading',
        title: '卸车',
        fields: [
            { key: 'unLoadingTime', label: '卸车时间(UTC)', kind: 'text', type: 'datetime', placeholder: DT },
            { key: 'bolRemark', label: 'BOL备注', kind: 'text', type: 'string' },
        ],
        photos: [{ keysKey: 'bolPhotoKeys', urlsKey: 'bolPhotoUrls', fieldKey: 'bolPhotosJson', label: 'BOL照片' }],
    },
};

/** 回退用：业务配置表无匹配时按途经点类型给全量步骤 */
const STEPS_BY_TYPE: Record<number, StepKey[]> = {
    1: ['arrived', 'loading', 'sealing', 'departure'],
    2: ['arrived', 'unsealing', 'unloading', 'loading', 'sealing', 'departure'],
    3: ['arrived', 'unsealing', 'unloading'],
};
const ALL_STEPS: StepKey[] = ['arrived', 'unsealing', 'unloading', 'loading', 'sealing', 'departure'];

/** 步骤条实心判定：该步的"实际"时间有值 */
const STEP_DONE_KEY: Record<StepKey, string> = {
    arrived: 'actualArrivalAt',
    loading: 'loadingTime',
    sealing: 'sealTime',
    departure: 'actualDepartureAt',
    unsealing: 'unSealTime',
    unloading: 'unLoadingTime',
};

/** 装载明细状态（tms_loading_detail.status，TmsLoadingDetailStatus）：事件同步进度，不是包牌/运单的业务状态 */
const LOADING_DETAIL_STATUS_MAP: Record<number, { label: string; color: string }> = {
    1: { label: '1-同步中', color: 'orange' },
    2: { label: '2-已同步', color: 'blue' },
    3: { label: '3-已完成', color: 'green' },
};

/** 容器类型（tms_task_container_info.container_type），与业务侧 TmsTaskContainerTypeEnum 一致 */
const CONTAINER_TYPE_MAP: Record<number, string> = { 1: '卡板箱', 2: '蛇皮袋', 3: '托盘', 4: 'Gaylord' };

/** 平台类业务（2-TikTok 3-Temu_GA 4-Temu_SCF）已完成（状态 4）：保存时二次弹窗警告，不拦截（与任务抽屉同） */
const PLATFORM_BUSINESS_TYPES = new Set([2, 3, 4]);
const COMPLETED_TASK_STATUS = 4;

/** antd 迷你分页页码之间没有外边距（1、2 贴在一起），用 Tailwind 任意变体补回间距，仍保持小号 */
const MINI_PAGINATION_SPACED = '[&_.ant-pagination-item]:!mx-1 [&_.ant-pagination-prev]:!mx-1 [&_.ant-pagination-next]:!mx-1 [&_.ant-pagination-jump-prev]:!mx-1 [&_.ant-pagination-jump-next]:!mx-1';

/** 时区缩写：与后端 ET/CT/MT/PT 口径一致，取 Intl 短名首字母 + T（PDT/PST → PT） */
const zoneAbbr = (zone: string, at: Date): string => {
    try {
        const part = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'short' }).formatToParts(at).find((item) => item.type === 'timeZoneName')?.value ?? '';
        return /^[A-Z]{3,4}$/.test(part) ? `${part[0]}T` : part;
    } catch {
        return '';
    }
};

/** UTC 时间点字符串 → 「yyyy-MM-dd HH:mm:ss 缩写」本地展示；时区缺失 / 格式不符返回 null */
const utcToLocalDisplay = (utcText: string, zone: string | null): string | null => {
    if (!zone || !UTC_PATTERN.test(utcText)) return null;
    try {
        const converted = dayjs.utc(utcText, 'YYYY-MM-DD HH:mm:ss').tz(zone);
        if (!converted.isValid()) return null;
        const abbr = zoneAbbr(zone, converted.toDate());
        return `${converted.format('YYYY-MM-DD HH:mm:ss')}${abbr ? ` ${abbr}` : ''}`;
    } catch {
        return null;
    }
};

/**
 * 业务侧每步操作会顺带改途经点状态，运维卡片只改时间不改状态会被业务页的步骤校验拦住
 * （如 PC 发车要求状态 ∈ {4 待发车, 3 已发车}，见业务 WaypointStepBar 的 Departure 前置判断）。保存时黄字提示，不拦截。
 */
const STATUS_HINTS: { card: StepKey; timeKey: string; expected: number[]; label: string; blocked: string }[] = [
    // blocked：只改时间不改状态时，业务侧（WaypointStepBar 前置校验）实际会拦住的下一步及其提示原文
    { card: 'arrived', timeKey: 'actualArrivalAt', expected: [2, 4, 3], label: '2-已到站',
        blocked: '本站后续「装车 / 解封车」会被拦，提示"未完成司机到达操作，请优先司机到达"' },
    { card: 'sealing', timeKey: 'sealTime', expected: [4, 3], label: '4-待发车',
        blocked: '本站「发车」会被拦，提示"未完成封车操作，请优先封车"' },
    { card: 'departure', timeKey: 'actualDepartureAt', expected: [3], label: '3-已发车',
        blocked: '下一站「司机到达」会被拦，PC 提示"上一站未发车，请联系上一站"，接口返回"上一站点未完成发车操作"' },
];

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const TIME_PATTERN = /^\d{2}:\d{2}(:\d{2})?$/;

// ---------- 值模型 ----------

/** 表单值全部按字符串持有：UTC 时间点取原值；本地日期/时间从 `*LocalAt`（date + time 拼接）拆出 */
const buildValues = (item: TmsTaskWaypointItem): Record<string, string> => {
    const raw = item as unknown as Record<string, unknown>;
    const values: Record<string, string> = {};
    const str = (v: unknown) => (v === null || v === undefined ? '' : String(v));
    [BASE_CARD, ...Object.values(STEP_CARDS)].forEach((card) => card.fields.forEach((f) => {
        if (f.localOf) {
            const local = str(raw[`${f.localOf.replace(/At$/, '')}LocalAt`]);
            const [date, time] = local ? local.split(' ') : ['', ''];
            values[f.key] = f.type === 'date' ? date ?? '' : time ?? '';
        } else {
            values[f.key] = str(raw[f.key]);
        }
    }));
    return values;
};

const buildPhotos = (item: TmsTaskWaypointItem): Record<string, string[]> => {
    const photos: Record<string, string[]> = {};
    Object.values(STEP_CARDS).forEach((card) => card.photos.forEach((p) => {
        photos[p.fieldKey] = [...((item[p.keysKey] as string[] | null) ?? [])];
    }));
    return photos;
};

const toTypedValue = (f: FieldDef, raw: string): string | number => (f.type === 'int' || f.type === 'long' ? Number(raw) : raw);

type Change = { key: string; label: string; before: string; after: string; typed: string | number | null };

/** 实际 vs 计划：晚于计划红、不晚绿（与业务侧 compareTimeColor 同），缺一方不着色 */
const compareColor = (planAt: string, actualAt: string): string | undefined => {
    if (!planAt || !actualAt) return undefined;
    return actualAt > planAt ? 'text-red-500' : 'text-green-600';
};

// ---------- 单个途经点编辑器 ----------

type EditorProps = {
    item: TmsTaskWaypointItem;
    options: TmsTaskFieldOptionsResponse['opsToolTmsTaskFieldOptions'] | undefined;
    task: NonNullable<Props['task']>;
    unlocked: boolean;
    onSaved: () => Promise<unknown>;
    /** 上报本途经点是否有卡片处于编辑态：抽屉据此决定「解除联动锁定」可否点击，并在全部退出编辑时自动上锁 */
    onEditingChange: (waypointRowId: TmsTaskWaypointItem['id'], editing: boolean) => void;
    /** 注册「上锁即放弃」处理器：抽屉上锁时汇总各编辑器解锁后的改动数、确认后逐个恢复快照；卸载时传 null 注销 */
    onRegisterRelock: (waypointRowId: TmsTaskWaypointItem['id'], handler: RelockHandler | null) => void;
};

/** 「上锁即放弃」处理器（用户 2026-10-21）：changedCount = 解锁后改了几个字段；revert = 恢复到解锁前快照 */
type RelockHandler = { changedCount: () => number; revert: () => void };

/** 库中有照片 key 但预签名链接数对不上：页面显示不全，编辑保存会以显示的为准覆盖库中记录（用户 2026-10-21：可编辑，红字提示） */
const photoMismatchHint = (keyCount: number, urlCount: number): string | null =>
    keyCount > 0 && urlCount !== keyCount ? `库中有 ${keyCount} 张照片，仅 ${urlCount} 张链接生成成功，页面显示不全` : null;

const WaypointEditor: React.FC<EditorProps> = ({ item, options, task, unlocked, onSaved, onEditingChange, onRegisterRelock }) => {
    // 用 App.useApp() 的 modal 而非静态 Modal.confirm：静态方法拿不到 ConfigProvider 主题，确认按钮会退回 antd 默认蓝而非主题紫
    const { modal } = App.useApp();
    // 步骤随 路线类型 + 业务类型 + 途经点类型，直接复用业务侧配置表 taskTabStepConfig（用户 2026-10-09：LD 等 TemuGA/SCF 终点站无解封车 / 卸车）；
    // 配置缺失（路线类型未知等）时回退到按途经点类型的全量步骤，保证运维仍能看到数据
    const steps = useMemo<StepKey[]>(() => {
        const resolved = resolveSteps(item.waypointType ?? 0, task.routeType ?? undefined, task.businessType ?? undefined)
            .map((key) => (key === BizStepKey.DriverArrived ? 'arrived' : key) as StepKey);
        return resolved.length > 0 ? resolved : (STEPS_BY_TYPE[item.waypointType ?? 0] ?? ALL_STEPS);
    }, [item.waypointType, task.routeType, task.businessType]);
    const [activeStep, setActiveStep] = useState<StepKey>(steps[0]);
    const [editingCard, setEditingCard] = useState<CardKey | null>(null);
    const [initial, setInitial] = useState<Record<string, string>>(() => buildValues(item));
    const [values, setValues] = useState<Record<string, string>>(() => buildValues(item));
    const [initialPhotos, setInitialPhotos] = useState<Record<string, string[]>>(() => buildPhotos(item));
    const [photos, setPhotos] = useState<Record<string, string[]>>(() => buildPhotos(item));
    const [runUpdate, { loading: saving }] = useMutation<UpdateTmsTaskWaypointFieldsResponse>(UPDATE_TMS_TASK_WAYPOINT_FIELDS, {
        onError: (e) => message.error(e.message),
    });

    // 下游刷新后同步回显（保存成功 / 他人修改）
    useEffect(() => {
        const v = buildValues(item);
        const p = buildPhotos(item);
        setInitial(v);
        setValues(v);
        setInitialPhotos(p);
        setPhotos(p);
        setEditingCard(null);
    }, [item]);

    // 编辑态上报：保存成功（item 刷新复位）/ 取消 / 切步骤自动取消 都会让 editingCard 回到 null；卸载时视为退出编辑
    useEffect(() => {
        onEditingChange(item.id, editingCard !== null);
    }, [editingCard, item.id, onEditingChange]);
    useEffect(() => () => onEditingChange(item.id, false), [item.id, onEditingChange]);

    // 「上锁即放弃」（用户 2026-10-21）：解锁那一刻拍快照；抽屉上锁时按快照恢复，解锁期间未保存的修改作废。
    // 快照只在 unlocked 翻转时更新，故不把 values 列入依赖
    const unlockSnapshotRef = useRef<Record<string, string> | null>(null);
    useEffect(() => {
        unlockSnapshotRef.current = unlocked ? { ...values } : null;
    }, [unlocked]); // eslint-disable-line react-hooks/exhaustive-deps
    // 每次渲染重新注册，保证抽屉拿到的是最新 values 的闭包
    useEffect(() => {
        const snapshot = unlockSnapshotRef.current;
        onRegisterRelock(item.id, {
            changedCount: () => (snapshot ? countChangedFields(snapshot, values) : 0),
            revert: () => { if (snapshot) setValues(snapshot); },
        });
    });
    useEffect(() => () => onRegisterRelock(item.id, null), [item.id, onRegisterRelock]);

    // 默认选中第一个未完成的步骤（全部完成则停在最后一步），与业务侧一致
    useEffect(() => {
        const firstPending = steps.find((s) => !(initial[STEP_DONE_KEY[s]] ?? ''));
        setActiveStep(firstPending ?? steps[steps.length - 1]);
    }, [item.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const zone = usableZone(item.zone);
    const isDeleted = item.isDeleted;

    // 「装车信息」只读汇总：切到装车步骤才查，来自装载明细 / 容器表，不随本卡片编辑保存
    const { data: loadingSummaryData, loading: loadingSummaryLoading } = useQuery<TmsTaskWaypointLoadingSummaryResponse>(TMS_TASK_WAYPOINT_LOADING_SUMMARY, {
        variables: { taskId: task.id, taskWaypointId: item.id },
        skip: activeStep !== 'loading',
        fetchPolicy: 'network-only',
        onError: (e) => message.error(e.message),
    });
    const loadingSummary = loadingSummaryData?.opsToolTmsTaskWaypointLoadingSummary;
    // 「重算下游静态 ETA」：显式按钮，运维点了才算（不随保存自动触发）；Java 侧按业务发车同一算法重算并回写 BOL
    const [runRecomputeEta, { loading: recomputingEta }] = useMutation<RecomputeTmsStaticEtaResponse>(RECOMPUTE_TMS_STATIC_ETA, {
        onError: (e) => message.error(e.message),
    });
    const confirmRecomputeEta = () => modal.confirm({
        title: '重算下游静态 ETA',
        icon: <ExclamationCircleFilled style={{ color: '#faad14' }} />,
        okText: '确认重算',
        content: `以本站实际发车时间 ${item.actualDepartureAt ?? '-'} UTC 为锚点，按路线行驶 / 停留时长重算下游各站静态 ETA，并回写 BOL 的下一站到达时间；下游站已发车时只算下一站。确认继续？`,
        onOk: async () => {
            const result = await runRecomputeEta({ variables: { taskId: task.id, taskWaypointId: item.id } });
            const payload = result.data?.opsToolRecomputeTmsStaticEta;
            if (!payload) return;
            if (!payload.success) {
                message.error(payload.message);
                return;
            }
            message.success(payload.message);
            await onSaved();
        },
    });

    /** UTC 时间点 → 本地日期/时间：输入完整格式即换算；清空则同步清空；时区缺失不换算（下有黄字提示），保持可手填 */
    const applyLinkage = (next: Record<string, string>, key: string, value: string) => {
        const locals = [BASE_CARD, ...Object.values(STEP_CARDS)].flatMap((c) => c.fields).filter((f) => f.localOf === key);
        if (locals.length === 0) return;
        const trimmed = value.trim();
        if (trimmed === '') {
            locals.forEach((f) => { next[f.key] = ''; });
        } else if (zone && UTC_PATTERN.test(trimmed)) {
            try {
                const converted = dayjs.utc(trimmed, 'YYYY-MM-DD HH:mm:ss').tz(zone);
                if (converted.isValid()) {
                    locals.forEach((f) => { next[f.key] = f.type === 'date' ? converted.format('YYYY-MM-DD') : converted.format('HH:mm:ss'); });
                }
            } catch {
                // 时区名不被浏览器识别：不换算，本地字段保持可手填
            }
        }
    };

    const setField = (f: FieldDef, v: string) => setValues((prev) => {
        const next = { ...prev, [f.key]: v };
        if (!unlocked) applyLinkage(next, f.key, v);
        return next;
    });

    /** 外键下拉：文案带 ID 便于按 ID 搜索；途经点ID 额外给「-1 · 三方站点」；当前值不在列表时保留为一项，避免打开即被清空 */
    const optionsFor = (f: FieldDef, current: string): { label: string; value: string }[] => {
        if (!f.optionsKey) return f.options ?? [];
        const list = (options?.[f.optionsKey] ?? []).map((o) => ({ value: String(o.value), label: `${o.value} · ${o.label}` }));
        if (f.key === 'waypointId') list.unshift({ value: EXTERNAL_WAYPOINT_ID, label: `${EXTERNAL_WAYPOINT_ID} · 三方站点（站点在「三方站点ID」中选）` });
        if (f.key === 'routeId') list.unshift({ value: THIRD_PARTY_ROUTE_ID, label: `${THIRD_PARTY_ROUTE_ID} · 三方任务（无路线）` });
        if (current && !list.some((o) => o.value === current)) list.unshift({ value: current, label: `${current} · (不在列表中)` });
        return list;
    };

    /** 只读态下拉值的展示：枚举取 label，外键取「ID · 名称」 */
    const optionLabel = (f: FieldDef, raw: unknown): string => {
        const text = raw === null || raw === undefined || raw === '' ? '-' : String(raw);
        if (text === '-') return text;
        return optionsFor(f, '').find((o) => o.value === text)?.label ?? text;
    };

    const cardOf = (key: CardKey): CardDef => (key === 'base' ? BASE_CARD : STEP_CARDS[key]);
    /** 当前途经点类型下应展示的字段 */
    const visibleFields = (card: CardDef): FieldDef[] => card.fields.filter((field) => !field.hideForWaypointTypes?.includes(item.waypointType ?? 0));
    /** 当前途经点类型下应展示的照片项 */
    const visiblePhotos = (card: CardDef): PhotoDef[] => card.photos.filter((photo) => !photo.hideForWaypointTypes?.includes(item.waypointType ?? 0));

    /** 当前编辑卡片的改动（字段 + 照片），照片以 JSON 字符串比对，空数组视为置空 */
    const changesOf = (key: CardKey): Change[] => {
        const card = cardOf(key);
        const list: Change[] = [];
        visibleFields(card).forEach((f) => {
            const before = initial[f.key] ?? '';
            const after = (values[f.key] ?? '').trim();
            if (before !== after) list.push({ key: f.key, label: f.label, before, after, typed: after === '' ? null : toTypedValue(f, after) });
        });
        visiblePhotos(card).forEach((p) => {
            const before = initialPhotos[p.fieldKey] ?? [];
            const after = photos[p.fieldKey] ?? [];
            if (JSON.stringify(before) !== JSON.stringify(after)) {
                list.push({
                    key: p.fieldKey,
                    label: p.label,
                    before: before.length ? `${before.length} 张` : '',
                    after: after.length ? `${after.length} 张` : '',
                    typed: after.length ? JSON.stringify(after) : null,
                });
            }
        });
        return list;
    };

    const changes = useMemo(() => (editingCard ? changesOf(editingCard) : []), [editingCard, values, photos, initial, initialPhotos]); // eslint-disable-line react-hooks/exhaustive-deps

    /** 切换步骤：上一步卡片若在编辑中（未保存）自动取消、丢弃草稿，新步骤可直接点「编辑」；「途经点信息」卡片不随步骤切换 */
    const selectStep = (stepKey: StepKey) => {
        if (editingCard !== null && editingCard !== 'base') cancelEdit();
        setActiveStep(stepKey);
    };

    const cancelEdit = () => {
        setValues(initial);
        setPhotos(initialPhotos);
        setEditingCard(null);
    };

    const doSave = async () => {
        const fields: TmsTaskWaypointFieldsInput = {};
        changes.forEach((c) => { fields[c.key] = c.typed; });
        const result = await runUpdate({ variables: { taskWaypointId: item.id, expectedUpdatedAt: item.updatedAt, fields } });
        const payload = result.data?.opsToolUpdateTmsTaskWaypointFields;
        if (!payload) return;
        if (!payload.success) {
            message.error(payload.message);
            return;
        }
        message.success(`途经点 ${item.id} 已更新 ${changes.length} 个字段`);
        await onSaved();
    };

    const handleSave = () => {
        if (!editingCard) return;
        if (changes.length === 0) {
            message.info('没有任何改动');
            return;
        }
        const card = cardOf(editingCard);
        const fieldByKey = Object.fromEntries(card.fields.map((f) => [f.key, f]));
        const missingRequired = changes.filter((c) => fieldByKey[c.key]?.required && c.after === '');
        if (missingRequired.length > 0) {
            message.warning(`以下字段不允许为空：${missingRequired.map((c) => c.label).join('、')}`);
            return;
        }
        const bad: string[] = [];
        changes.forEach((c) => {
            const f = fieldByKey[c.key];
            if (!f || c.after === '') return;
            if ((f.type === 'int' || f.type === 'long') && !/^-?\d+$/.test(c.after)) bad.push(`${c.label}「${c.after}」须为整数`);
            if (f.type === 'date' && !DATE_PATTERN.test(c.after)) bad.push(`${c.label}「${c.after}」须为 ${D}`);
            if (f.type === 'time' && !TIME_PATTERN.test(c.after)) bad.push(`${c.label}「${c.after}」须为 ${T}`);
            if (f.type === 'datetime' && !UTC_PATTERN.test(c.after)) bad.push(`${c.label}「${c.after}」须为 ${DT}`);
        });
        if (bad.length > 0) {
            message.warning(bad.join('；'));
            return;
        }
        // 途经点ID 与 三方站点ID 配套（用户 2026-10-08）：三方站点ID 有值 ⇔ 途经点ID 为 -1；途经点ID 为仓库 ⇒ 三方站点ID 必须为空
        if (editingCard === 'base') {
            const waypointId = (values.waypointId ?? '').trim();
            const extWaypointId = (values.extWaypointId ?? '').trim();
            if (extWaypointId && waypointId !== EXTERNAL_WAYPOINT_ID) {
                message.warning('三方站点ID 有值时，途经点ID 必须为 -1');
                return;
            }
            if (waypointId && waypointId !== EXTERNAL_WAYPOINT_ID && extWaypointId) {
                message.warning('途经点ID 为仓库时，三方站点ID 必须为空');
                return;
            }
            if (waypointId === EXTERNAL_WAYPOINT_ID && !extWaypointId) {
                message.warning('途经点ID 为 -1 时，三方站点ID 必须有值');
                return;
            }
        }
        const platformCompleted = PLATFORM_BUSINESS_TYPES.has(task.businessType ?? -1) && task.taskStatus === COMPLETED_TASK_STATUS;
        const statusHint = STATUS_HINTS.find((hint) => hint.card === editingCard && (values[hint.timeKey] ?? '') !== '');
        const currentStatus = Number(values.waypointStatus ?? initial.waypointStatus ?? '');
        const statusWarning = statusHint && !statusHint.expected.includes(currentStatus)
            ? `业务侧「${card.title}」操作会把途经点状态改为「${statusHint.label}」，当前为「${WAYPOINT_STATUS_MAP[currentStatus]?.label ?? currentStatus}」。只改时间不改状态，${statusHint.blocked}。如需放行，请在「途经点信息」卡片同步修改状态。`
            : null;
        modal.confirm({
            title: '风险操作确认',
            icon: <ExclamationCircleFilled style={{ color: '#ff4d4f' }} />,
            okText: '确认保存',
            okButtonProps: { danger: true },
            width: 720,
            content: (
                <div className="space-y-2">
                    <div>
                        即将直接改写途经点 {item.id}（任务 {task.id} / 顺序 {item.waypointOrder ?? '-'}）「{card.title}」的 {changes.length} 个字段。
                        不校验业务状态、不发事件、不维护关联一致性（不反推任务状态、不重算 ETA），确认继续？
                    </div>
                    {unlocked && (
                        <Alert type="error" showIcon message="本次在「解除联动锁定」状态下保存：系统未做任何自动换算，所有值以你填写的为准。" />
                    )}
                    {statusWarning && <Alert type="warning" showIcon message="途经点状态未同步（仅提示，不拦截）" description={statusWarning} />}
                    {editingCard === 'departure' && changes.some((c) => c.key === 'actualDepartureAt') && (
                        <Alert type="warning" showIcon message="本工具不自动重算下游静态 ETA 与 BOL 下一站到达时间；保存后可点本卡片的「重算下游静态 ETA」。" />
                    )}
                    <Table
                        size="small"
                        pagination={false}
                        rowKey="key"
                        dataSource={changes}
                        columns={[
                            { title: '字段', width: 200, render: (_, r) => `${r.label}（${r.key}）` },
                            { title: '改前', dataIndex: 'before', render: (v: string) => v || <span className="text-gray-400">空</span> },
                            { title: '改后', dataIndex: 'after', render: (v: string) => v || <span className="text-gray-400">空</span> },
                        ]}
                    />
                </div>
            ),
            // 任何保存都再确认一次（用户 2026-10-09，与任务抽屉一致）；平台类已完成任务再多一层
            onOk: () => new Promise<void>((resolve) => {
                const extraConfirms: { title: string; content: string }[] = [];
                if (platformCompleted) {
                    extraConfirms.push({
                        title: '再次确认：平台类任务已完成',
                        content: `任务 ${task.id}（${task.taskNumber ?? '-'}）属于 TikTok / Temu_GA / Temu_SCF 且已完成，相关数据可能已回传平台或进入结算，改动不会同步到平台侧。确定继续保存？`,
                    });
                }
                extraConfirms.push({
                    title: '再次确认：直接改写途经点数据',
                    content: `这 ${changes.length} 个字段将直接写入 tms_task_waypoint，不经业务校验，没有自动撤销手段，只能再次手工订正。确定保存？`,
                });
                const step = (index: number) => {
                    if (index >= extraConfirms.length) {
                        doSave().then(() => resolve(), () => resolve());
                        return;
                    }
                    modal.confirm({
                        title: extraConfirms[index].title,
                        icon: <ExclamationCircleFilled style={{ color: '#ff4d4f' }} />,
                        okText: '我已知晓，继续',
                        okButtonProps: { danger: true },
                        content: extraConfirms[index].content,
                        onOk: () => step(index + 1),
                        onCancel: () => resolve(),
                    });
                };
                step(0);
            }),
        });
    };

    /** 只读态一行：标签 + 值 */
    const readRow = (label: string, content: React.ReactNode) => (
        <div key={label} className="flex items-center text-sm py-1">
            <span className="text-gray-500 w-[9em] shrink-0">{label}：</span>
            <span className="text-gray-800">{content}</span>
        </div>
    );

    /** 只读态时间展示：UTC 原值 + 本地（含时区缩写），实际 vs 计划着色 */
    const readTimeTriple = (prefix: string, label: string, colorClass?: string) => {
        const raw = item as unknown as Record<string, string | null>;
        const utcValue = raw[`${prefix}At`] ?? '';
        const local = raw[`${prefix}LocalAt`] ?? '';
        const abbr = raw[`${prefix}Timezone`] ?? '';
        return readRow(label, utcValue ? (
            <span className={colorClass}>
                {utcValue} UTC
                {local && <span className="ml-3 text-gray-500">本地 {local}{abbr ? ` ${abbr}` : ''}</span>}
            </span>
        ) : '-');
    };

    const renderReadCard = (card: CardDef) => {
        const raw = item as unknown as Record<string, unknown>;
        const rendered = new Set<string>();
        const rows: React.ReactNode[] = [];
        visibleFields(card).forEach((f) => {
            if (f.localOf) return; // 本地日期/时间合并进 UTC 行展示
            if (f.type === 'datetime' && card.fields.some((o) => o.localOf === f.key)) {
                const prefix = f.key.replace(/At$/, '');
                const color = prefix.startsWith('actual')
                    ? compareColor(String(raw[`${prefix.replace('actual', 'plan')}At`] ?? ''), String(raw[f.key] ?? ''))
                    : undefined;
                rows.push(readTimeTriple(prefix, f.label.replace('(UTC)', ''), color));
                rendered.add(f.key);
                return;
            }
            const v = raw[f.key];
            const text = v === null || v === undefined || v === '' ? '-' : String(v);
            if (f.kind === 'select') {
                rows.push(readRow(f.label, f.key === 'waypointStatus' ? renderWaypointStatus(v as number | null) : optionLabel(f, v)));
            } else if (f.type === 'datetime' && text !== '-') {
                // 装车 / 封车 / 解封 / 卸车：库里只有 UTC 一列，本地时间按途经点时区现算，仅展示不写库（用户 2026-10-09）
                const local = utcToLocalDisplay(text, zone);
                rows.push(readRow(f.label.replace('(UTC)', ''), (
                    <span>
                        {text} UTC
                        {local && <span className="ml-3 text-gray-500">本地 {local}</span>}
                        <span className="ml-3 text-xs text-gray-400">（库无本地日期/时间列，仅展示不写库）</span>
                    </span>
                )));
            } else {
                rows.push(readRow(f.label, text));
            }
        });
        visiblePhotos(card).forEach((p) => {
            const keys = (item[p.keysKey] as string[] | null) ?? [];
            const urls = (item[p.urlsKey] as string[] | null) ?? [];
            const mismatch = photoMismatchHint(keys.length, urls.length);
            rows.push(readRow(p.label, (
                <span className="inline-flex items-center gap-2">
                    {urls.length === 0 ? '-' : (
                        <Image.PreviewGroup>
                            <span className="inline-flex gap-2">
                                {urls.map((url) => <Image key={url} src={url} width={100} height={100} className="object-cover rounded" />)}
                            </span>
                        </Image.PreviewGroup>
                    )}
                    {mismatch && <span className="text-red-500 text-xs">{mismatch}</span>}
                </span>
            )));
        });
        return <div>{rows}</div>;
    };

    const renderEditCard = (card: CardDef) => (
        // 右侧留出 保存 / 取消（绝对定位在卡片右上角）的宽度，提示条和字段不与之重叠
        <div className="space-y-3 pr-32">
            {card.key !== 'base' && card.fields.some((f) => f.localOf) && !zone && (
                <Alert type="warning" showIcon message="该途经点时区缺失（仓库 / 外部站点无 IANA 时区），本地日期/时间不自动换算，可手填。" />
            )}
            {card.key !== 'base' && card.fields.some((f) => f.localOf) && zone && !unlocked && (
                <div className="text-gray-500 text-xs">本地日期 / 时间由 UTC 按该途经点时区（{zone}）自动算出并锁定，只需填 UTC；要手动改本地时间，请打开右上角「解除联动锁定」。</div>
            )}
            {card.key !== 'base' && card.fields.some((f) => f.localOf) && zone && unlocked && (
                <Alert type="warning" showIcon message={`已解除联动锁定：修改 UTC 不再自动算出本地日期 / 时间，三个值各填各的，系统不校验它们是否对得上，保存前请自行核对。${RELOCK_DISCARD_HINT}`} />
            )}
            {card.key === 'base' && unlocked && (
                <Alert type="warning" showIcon message={`已解除联动锁定：顺序 / 类型 / 途经点ID / 三方站点ID / 路线ID 等默认锁定字段可直接改，系统不校验它们与路线 / 任务是否一致，保存前请自行核对。${RELOCK_DISCARD_HINT}`} />
            )}
            {/* 编辑态字段一行一个（用户 2026-10-09）；右侧留出空间，避免与绝对定位的 保存 / 取消 重叠 */}
            <div className="grid grid-cols-1 gap-3 max-w-[460px]">
                {visibleFields(card).map((f) => {
                    const disabled = !unlocked && (!!f.readOnly || (f.localOf !== undefined && !!zone));
                    return (
                        <div key={f.key}>
                            {f.kind === 'select' ? (
                                <SingleSelect
                                    label={f.label}
                                    options={optionsFor(f, values[f.key] ?? '')}
                                    value={values[f.key] || undefined}
                                    onChange={(v) => setField(f, v ?? '')}
                                    spanClassName="w-[9em]"
                                    placeholder={disabled ? '' : undefined}
                                    disabled={disabled}
                                    allowClear={!f.required}
                                    forceHorizontal
                                />
                            ) : f.type === 'date' || f.type === 'time' || f.type === 'datetime' ? (
                                <OpsDateTimeItem
                                    label={f.label}
                                    kind={f.type}
                                    value={values[f.key] ?? ''}
                                    onChange={(v) => setField(f, v)}
                                    disabled={disabled}
                                />
                            ) : (
                                <InputFilterItem
                                    label={f.label}
                                    value={values[f.key] ?? ''}
                                    onChange={(e) => setField(f, e.target.value)}
                                    spanClassName="w-[9em]"
                                    placeholder={disabled ? '' : (f.placeholder ?? '')}
                                    disabled={disabled}
                                    forceHorizontal
                                />
                            )}
                        </div>
                    );
                })}
            </div>
            {visiblePhotos(card).map((p) => {
                const photoKeys = (item[p.keysKey] as string[] | null) ?? [];
                const photoUrls = (item[p.urlsKey] as string[] | null) ?? [];
                const mismatch = photoMismatchHint(photoKeys.length, photoUrls.length);
                return (
                    <div key={p.fieldKey} className="flex items-start text-sm">
                        <span className="text-gray-500 w-[9em] shrink-0 pt-2">{p.label}：</span>
                        <div>
                            <ImageUploader
                                value={photos[p.fieldKey] ?? []}
                                displayUrls={photoUrls}
                                onChange={(keys) => setPhotos((prev) => ({ ...prev, [p.fieldKey]: keys }))}
                                maxCount={9}
                            />
                            {mismatch && (
                                <div className="text-red-500 text-xs mt-1">{mismatch}；在此编辑并保存会以当前显示的照片覆盖库中记录，不建议编辑。</div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );

    /** 步骤卡片圆角：选中首步时左上与标签相接不圆角，末步时右上不圆角（同业务侧） */
    const stepCardRounding = steps.indexOf(activeStep) === 0
        ? 'rounded-tr-md rounded-b-md'
        : steps.indexOf(activeStep) === steps.length - 1
            ? 'rounded-tl-md rounded-b-md'
            : 'rounded-md';

    const renderCard = (card: CardDef) => {
        const editing = editingCard === card.key;
        const otherEditing = editingCard !== null && !editing;
        // 步骤卡片与步骤条选中标签同色（gray-100）并相接，对齐业务侧 WaypointStepBar；「途经点信息」卡片保持浅灰独立
        return (
            <div key={card.key} className={`relative ${card.key === 'base' ? 'bg-gray-50 rounded-lg p-4 mb-4' : `bg-gray-100 p-4 mb-4 ${stepCardRounding}`}`}>
                {/* 编辑 / 保存 / 取消 绝对定位在卡片右上角，不单独占一行（用户 2026-10-09）；步骤卡片标题与步骤条选中标签重复已去掉，「途经点信息」无步骤标签保留标题 */}
                <div className="absolute top-3 right-4 z-10">
                    {editing ? (
                        <span className="space-x-2">
                            <Button type="primary" size="small" loading={saving} disabled={changes.length === 0} onClick={handleSave}>保存</Button>
                            <Button size="small" onClick={cancelEdit}>取消</Button>
                        </span>
                    ) : (
                        <span className="space-x-2">
                            {card.key === 'departure' && (
                                <Button size="small" loading={recomputingEta} disabled={otherEditing || !item.actualDepartureAt} title={item.actualDepartureAt ? '' : '无实际发车时间'} onClick={confirmRecomputeEta}>
                                    重算下游静态 ETA
                                </Button>
                            )}
                            <Button size="small" disabled={otherEditing} onClick={() => setEditingCard(card.key)}>编辑</Button>
                        </span>
                    )}
                </div>
                {card.key === 'base' && <div className="font-semibold mb-2">{card.title}</div>}
                {editing ? renderEditCard(card) : renderReadCard(card)}
                {card.key === 'loading' && renderLoadingSummary()}
            </div>
        );
    };

    /** 容器行按目的地相邻排列，同目的地的「目的地 / 目的仓」两列合并单元格（首行 rowSpan = 组内行数，其余 0） */
    const containerRows = useMemo(() => {
        const rows = [...(loadingSummary?.containers ?? [])].sort((a, b) =>
            (a.sortingCode ?? '').localeCompare(b.sortingCode ?? '') || (a.id ?? 0) - (b.id ?? 0));
        const span: Record<string, number> = {};
        let start = 0;
        while (start < rows.length) {
            let end = start;
            while (end + 1 < rows.length && (rows[end + 1].sortingCode ?? '') === (rows[start].sortingCode ?? '')) end += 1;
            for (let cursor = start; cursor <= end; cursor += 1) {
                span[String(rows[cursor].id)] = cursor === start ? end - start + 1 : 0;
            }
            start = end + 1;
        }
        return { rows, span };
    }, [loadingSummary]);
    const mergeByDestination = (row: { id: number | null }) => ({ rowSpan: containerRows.span[String(row.id)] ?? 1 });

    /** 装车信息（只读）：合计 + 装车容器 + 装载明细原始行；与业务侧「装车信息」对应，但不按目的地分组、不可编辑 */
    /** 子行：标签列缩进一级（对齐业务侧装车卡片的「装车信息:」下挂 运单数量 / 容器数量 / 容器信息 / 包牌信息） */
    const subRow = (label: string, content: React.ReactNode, alignTop = false) => (
        <div key={label} className={`flex ${alignTop ? 'items-start' : 'items-center'} text-sm py-1 pl-4`}>
            <span className={`text-gray-500 w-[7em] shrink-0 ${alignTop ? 'pt-1' : ''}`}>{label}：</span>
            <span className="text-gray-800 min-w-0 flex-1">{content}</span>
        </div>
    );

    /** 装车信息：样式对齐业务侧装车卡片（标签列 + 合计 + 无边框容器表 + 有边框包牌表）；数据来自装载明细 / 容器表，不随本卡片保存 */
    const renderLoadingSummary = () => (
        <div className="mt-2">
            <div className="flex items-center text-sm py-1">
                <span className="text-gray-500 w-[9em] shrink-0">装车信息：</span>
            </div>
            {loadingSummaryLoading && !loadingSummary ? <div className="pl-4"><Spin size="small" /></div> : loadingSummary && (
                <div>
                    {subRow('运单数量', <span className="text-purple-600">{loadingSummary.totalParcelQuantity}</span>)}
                    {subRow('包牌数量', loadingSummary.totalBagQuantity)}
                    {subRow('容器数量', loadingSummary.totalContainerQuantity)}
                    {subRow('容器信息', (
                        <Table
                            size="small"
                            pagination={false}
                            rowKey={(r) => String(r.id)}
                            dataSource={containerRows.rows}
                            locale={{ emptyText: '暂无数据' }}
                            className="max-w-[600px]"
                            columns={[
                                { title: '目的地', dataIndex: 'sortingCode', width: 160, render: (v: string | null) => v ?? '-', onCell: mergeByDestination },
                                { title: '容器数量', dataIndex: 'containerCount', width: 160, render: (v: number | null) => v ?? '-' },
                                { title: '容器单位', dataIndex: 'containerType', width: 160, render: (v: number | null) => (v === null ? '-' : CONTAINER_TYPE_MAP[v] ?? v) },
                            ]}
                        />
                    ), true)}
                    {subRow('包牌信息', (
                        <div>
                            <Table
                                size="small"
                                bordered
                                className="max-w-[760px]"
                                pagination={loadingSummary.details.length > 20 ? { pageSize: 20, size: 'small', className: MINI_PAGINATION_SPACED } : false}
                                rowKey={(r) => String(r.loadingDetailId)}
                                dataSource={loadingSummary.details}
                                locale={{ emptyText: '暂无数据' }}
                                columns={[
                                    { title: '包牌号/运单号', width: 240, render: (_, r) => <span title={`明细ID ${r.loadingDetailId ?? '-'} · ${r.bagId !== null ? '包牌' : '运单'} · 装车 ${r.loadingTime ?? '-'}`}>{r.bagNumber ?? r.trackingNumber ?? '-'}</span> },
                                    { title: '目的地中心', dataIndex: 'sortingCode', width: 180, render: (v: string | null) => v ?? '-' },
                                    { title: '运单数量', dataIndex: 'parcelQuantity', width: 110, render: (v: number | null) => v ?? '-' },
                                    { title: '状态', dataIndex: 'status', width: 110, render: (v: number | null) => (v === null ? '-' : LOADING_DETAIL_STATUS_MAP[v] ? <Tag color={LOADING_DETAIL_STATUS_MAP[v].color}>{LOADING_DETAIL_STATUS_MAP[v].label}</Tag> : <Tag>{v}</Tag>) },
                                ]}
                            />
                        </div>
                    ), true)}
                </div>
            )}
        </div>
    );

    return (
        <div>
            {isDeleted && <Alert className="mb-3" type="error" showIcon message="该途经点已逻辑删除，仍可订正；删除标记随任务删除/恢复，此处不单独改。" />}
            {renderCard(BASE_CARD)}
            {/* 步骤条：对齐业务侧 WaypointStepBar 的样式（紫色实心圆点 + 线段箭头 + 下方标签，选中步骤标签带灰底），只借样式不借点击校验 */}
            <div className="flex items-start mt-6">{/* 连接线定宽，不随抽屉拉满（同业务侧 ARROW_COLUMN_WIDTH）；与上方「途经点信息」卡片拉开间距 */}
                {steps.map((stepKey, index) => {
                    const done = !!(initial[STEP_DONE_KEY[stepKey]] ?? '');
                    const selected = stepKey === activeStep;
                    const last = index === steps.length - 1;
                    return (
                        <React.Fragment key={stepKey}>
                            <div
                                role="button"
                                tabIndex={0}
                                className="flex flex-col items-center flex-shrink-0 cursor-pointer w-[110px]"
                                onClick={() => selectStep(stepKey)}
                                onKeyDown={(event) => event.key === 'Enter' && selectStep(stepKey)}
                            >
                                <span className={`inline-block w-4 h-4 rounded-full border-2 ${done ? 'bg-[#722ed1] border-[#722ed1]' : 'bg-white border-gray-300'}`} />
                                <span className={`mt-2 w-full text-sm text-center whitespace-nowrap py-2 rounded-t-md ${selected ? 'bg-gray-100' : ''} ${done ? 'text-gray-800 font-medium' : 'text-gray-400'}`}>
                                    {STEP_CARDS[stepKey].title}
                                </span>
                            </div>
                            {!last && (
                                <div className="flex items-center w-[120px] h-4">
                                    <div className={`flex-1 h-0.5 ${done ? 'bg-[#722ed1]' : 'bg-gray-300'}`} />
                                    <div className={`w-0 h-0 border-t-[6px] border-b-[6px] border-l-[10px] border-t-transparent border-b-transparent ${done ? 'border-l-[#722ed1]' : 'border-l-gray-300'}`} />
                                </div>
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
            {renderCard(STEP_CARDS[activeStep])}
        </div>
    );
};

// ---------- 抽屉 ----------

const TaskWaypointDrawer: React.FC<Props> = ({ task, onClose }) => {
    const { modal } = App.useApp();
    const [unlocked, setUnlocked] = useState(false);
    // 正在编辑的途经点 id 集合：Tabs 不销毁非活动 pane，多个编辑器同时挂载，用集合避免互相覆盖；集合空 → 自动上锁
    const [editingWaypointIds, setEditingWaypointIds] = useState<TmsTaskWaypointItem['id'][]>([]);
    const anyEditing = editingWaypointIds.length > 0;
    const handleEditingChange = useCallback((waypointRowId: TmsTaskWaypointItem['id'], editing: boolean) => {
        setEditingWaypointIds((prev) => {
            if (editing) return prev.includes(waypointRowId) ? prev : [...prev, waypointRowId];
            return prev.includes(waypointRowId) ? prev.filter((id) => id !== waypointRowId) : prev;
        });
    }, []);
    useEffect(() => {
        if (!anyEditing) setUnlocked(false);
    }, [anyEditing]);
    // 各编辑器注册的「上锁即放弃」处理器（用户 2026-10-21）；用 ref 而非 state，注册不触发重渲染
    const relockHandlersRef = useRef(new Map<TmsTaskWaypointItem['id'], RelockHandler>());
    const handleRegisterRelock = useCallback((waypointRowId: TmsTaskWaypointItem['id'], handler: RelockHandler | null) => {
        if (handler) relockHandlersRef.current.set(waypointRowId, handler);
        else relockHandlersRef.current.delete(waypointRowId);
    }, []);
    /**
     * 解除联动锁定：打开时二次确认（用户 2026-10-09，同任务抽屉「解除业务类型锁定」的做法）；
     * 关闭 = 各编辑器恢复到解锁前快照，解锁期间未保存的修改作废，有改动先确认（用户 2026-10-21）
     */
    const handleUnlockChange = (on: boolean) => {
        if (!on) {
            const handlers = Array.from(relockHandlersRef.current.values());
            const relock = () => {
                handlers.forEach((handler) => handler.revert());
                setUnlocked(false);
            };
            const changedCount = handlers.reduce((sum, handler) => sum + handler.changedCount(), 0);
            if (changedCount === 0) {
                relock();
                return;
            }
            modal.confirm({
                title: '上锁将放弃解锁期间的修改',
                icon: <ExclamationCircleFilled style={{ color: '#ff4d4f' }} />,
                okText: `放弃 ${changedCount} 处修改并上锁`,
                okButtonProps: { danger: true },
                content: `解锁后改过的 ${changedCount} 个字段将恢复为解锁前的值。要保留这些修改，请先点卡片「保存」再上锁。确认上锁？`,
                onOk: relock,
            });
            return;
        }
        modal.confirm({
            title: '风险操作确认',
            icon: <ExclamationCircleFilled style={{ color: '#ff4d4f' }} />,
            okText: '确认解除',
            okButtonProps: { danger: true },
            content: '解除后，顺序 / 类型 / 途经点ID / 路线ID 等默认锁定的字段可直接改，UTC 时间不再自动换算本地日期/时间，所有值以你填写的为准。确认解除联动锁定？',
            onOk: () => setUnlocked(true),
        });
    };
    const { data: optionsData } = useQuery<TmsTaskFieldOptionsResponse>(TMS_TASK_FIELD_OPTIONS, {
        skip: task === null,
        fetchPolicy: 'cache-first',
        onError: (e) => message.error(e.message),
    });
    const { data, loading, refetch } = useQuery<TmsTaskWaypointsResponse>(TMS_TASK_WAYPOINTS, {
        variables: { taskId: task?.id },
        skip: task === null,
        fetchPolicy: 'network-only',
        onError: (e) => message.error(e.message),
    });
    const waypoints = data?.opsToolTmsTaskWaypoints ?? [];

    return (
        <Drawer
            title={(
                <div className="flex justify-between items-center pr-6">
                    <span>{task ? `任务途经点（任务ID ${task.id}${task.taskNumber ? ` / ${task.taskNumber}` : ''}）` : '任务途经点'}</span>
                    <span className="flex items-center space-x-2 text-sm font-normal">
                        <Switch size="small" checked={unlocked} onChange={handleUnlockChange} disabled={waypoints.length === 0 || !anyEditing} />
                        <span className={unlocked ? 'text-orange-500' : 'text-gray-500'} title={anyEditing ? '' : '先点击卡片「编辑」后才可解除'}>解除联动锁定</span>
                    </span>
                </div>
            )}
            open={task !== null}
            onClose={onClose}
            width={1100}
            destroyOnClose
        >
            {loading && waypoints.length === 0 ? (
                <Spin />
            ) : waypoints.length === 0 ? (
                <Alert type="info" showIcon message="该任务没有途经点行" />
            ) : (
                <Tabs
                    items={waypoints.map((wp) => ({
                        key: String(wp.id),
                        // 标签对齐业务侧任务详情：只显示站点码（仓库 / 外部站点 sortingCode），查不到回退「途经点 ID」；已删除带红点
                        label: (
                            <span title={`#${wp.waypointOrder ?? '-'} ${WAYPOINT_TYPE_MAP[wp.waypointType ?? 0] ?? ''} · 途经点ID ${wp.waypointId ?? '-'}`}>
                                {wp.isDeleted && <Badge status="error" className="mr-1" />}
                                {wp.waypointName ?? `途经点 ${wp.waypointId ?? wp.id}`}
                            </span>
                        ),
                        children: task ? <WaypointEditor item={wp} options={optionsData?.opsToolTmsTaskFieldOptions} task={task} unlocked={unlocked} onSaved={() => refetch()} onEditingChange={handleEditingChange} onRegisterRelock={handleRegisterRelock} /> : null,
                    }))}
                />
            )}
        </Drawer>
    );
};

export default TaskWaypointDrawer;
